import { useReactiveVar } from '@apollo/client';
import { Headline3, Meta, theme } from 'aimo-ui';
import CopyText from 'components/common/CopyText';
import MenuItem from 'components/common/MenuItem';
import config from 'config/config';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useSupportStatus from 'hooks/useSupportStatus';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import { featureCountryCode } from 'states/persistInStorage';
import {
  ColumnWithMargins,
  ScreenContainer,
  SheetSection,
} from 'styles/ContainerStyles';
import {
  CountryRegionDynamicInfo,
  parseFeatureFlagObject,
} from 'utils/featureFlagUtils';
import OpenHours from './OpenHours';

const ChatOption = () => {
  const { t } = useTranslation();
  const { open } = useSupportStatus();

  const handlePress = async () => {
    await open();
  };

  return (
    <MenuItem
      menuLabel={t('account.chatbot')}
      withArrow
      iconName={'Chat-Filled'}
      onPress={handlePress}
    />
  );
};

const ContactUsContent: React.FC = () => {
  const {
    flags: { country_region_dynamic_info },
  } = useContext(FeatureFlagsContext);
  const { t } = useTranslation();
  const { isAvailable } = useSupportStatus();
  const currentCountry = useReactiveVar(featureCountryCode);
  const version = config.appVersion;
  const countryRegionDynamicInfo =
    parseFeatureFlagObject<CountryRegionDynamicInfo>(
      country_region_dynamic_info,
      'country_region_dynamic_info'
    );
  const countryInfo =
    currentCountry && countryRegionDynamicInfo[currentCountry];

  const openPhone = async () => {
    const url = `tel:${encodeURIComponent(
      countryInfo?.phoneNumber?.replace(/\s/g, '') ?? ''
    )}`;
    await Linking.openURL(url);
  };

  const openEmail = async () => {
    const url = `mailto:${countryInfo?.emailAddress}`;
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
    }
  };

  const getFormattedCallPrice = (
    callPrice: { perCall: string; perMin: string } | null
  ) => {
    if (!callPrice) {
      return;
    }
    return t('account.contactUs.getInTouch.callPrice', {
      perCall: callPrice.perCall,
      perMin: callPrice.perMin,
    });
  };

  return (
    <ScreenContainer background={theme.colors.gray50} mb={20}>
      <SheetSection pt={0}>
        <OpenHours openHours={countryInfo?.openHours} />
      </SheetSection>
      <SheetSection pt={30}>
        <Headline3>{t('account.contactUs.getInTouch.title')}</Headline3>
        <ColumnWithMargins mt={8}>
          {/* Show ChatOption when support is available via hook */}
          {isAvailable && <ChatOption />}
          <MenuItem
            menuLabel={`${countryInfo?.phoneNumber}`}
            withArrow
            subtitle={getFormattedCallPrice(countryInfo!.callPrice)}
            onPress={openPhone}
            testID={'contact-card-phone'}
            iconName="Phone-Filled"
          />
          <MenuItem
            iconName="Email-Filled"
            withArrow
            menuLabel={`${countryInfo?.emailAddress}`}
            onPress={openEmail}
            testID={'contact-card-email'}
            noBorder
          />
        </ColumnWithMargins>
      </SheetSection>
      <SheetSection mb={0} pt={30}>
        <Headline3>{t('account.contactUs.appInfo.appVersion')}</Headline3>
        <ColumnWithMargins mt={10}>
          <CopyText copyText={version}>
            <Meta color={theme.colors.gray300}>{version}</Meta>
          </CopyText>
        </ColumnWithMargins>
      </SheetSection>
    </ScreenContainer>
  );
};

export default ContactUsContent;
